import React, { useEffect, useMemo, useState } from "react";
import "../styles/BuyCreditsPage.css";
import {
  FiInfo, FiShield, FiCheck, FiX, FiGift, FiZap, FiCheckCircle,
  FiChevronDown, FiCreditCard,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import axiosInstance from "../utils/axiosInstance";

function normalizeRole(roleRaw) {
  const r = String(roleRaw || "").trim().toLowerCase();
  if (r === "tedarikçi" || r === "tedarikci" || r === "supplier") return "supplier";
  if (r === "klinik" || r === "clinic") return "clinic";
  if (r === "doktor" || r === "doctor") return "doctor";
  return "";
}

// KDV oranı — backend payment/constants.py ile aynı tutulmalıdır (tek kaynak orada).
const VAT_RATE = 0.20;
// Standart paket tüm dillerde KDV DAHİL 300 TL (net 250 + KDV 50).
const STANDARD_BASE_PRICE_TRY = 250;
const STANDARD_PRICE_TRY = Math.round(STANDARD_BASE_PRICE_TRY * (1 + VAT_RATE) * 100) / 100;
// EN/DE arayüzde standart paket KDV DAHİL 300 TL (net 250 + KDV 50) — backend
// payment/constants.py INTERNATIONAL_PRICE_TRY ile aynı tutulmalıdır.
const INTERNATIONAL_PRICE_TRY = 300;
const INTERNATIONAL_BASE_PRICE_TRY = Math.round((INTERNATIONAL_PRICE_TRY / (1 + VAT_RATE)) * 100) / 100;

// Paket geçerlilik süresi (ay) — backend payment/constants.py PACKAGES'teki
// duration_months ile aynı tutulmalıdır (doctor_clinic ve supplier ikisi de 6 ay).
const PACKAGE_DURATION_MONTHS = 6;

// Arayüz dili EN/DE ise standart paket 300 TL (yalnızca doctor_clinic). Tüm ödemeler TL.
function isInternationalLanguage(lang) {
  const l = String(lang || "").toLowerCase();
  return l.startsWith("en") || l.startsWith("de");
}

// serverTotal: sunucu 409 price_mismatch ile farklı bir tutar bildirdiyse o tutar
// gösterilir (fiyatı her zaman sunucu belirler; ekran sadece onu yansıtır).
function buildPackages(international, t, serverTotal = null) {
  const totalPrice = serverTotal != null
    ? serverTotal
    : (international ? INTERNATIONAL_PRICE_TRY : STANDARD_PRICE_TRY);
  const basePrice = serverTotal != null
    ? Math.round((serverTotal / (1 + VAT_RATE)) * 100) / 100
    : (international ? INTERNATIONAL_BASE_PRICE_TRY : STANDARD_BASE_PRICE_TRY);
  return {
    doctor_clinic: {
      id: "doctor_clinic",
      title: t("buyCredits.packages.doctorClinicTitle"),
      credits: 1,
      currency: "TRY",
      basePrice,
      vatAmount: Math.round((totalPrice - basePrice) * 100) / 100,
      totalPrice,
      currencyNote: t("buyCredits.packages.currencyNote", { amount: totalPrice }),
      priceLabel: t("buyCredits.packages.oneTime"),
      usageLines: [
        t("buyCredits.packages.doctorClinicUsage1"),
        t("buyCredits.packages.doctorClinicUsage2"),
        t("buyCredits.packages.durationMonths", { count: PACKAGE_DURATION_MONTHS }),
      ],
      allowedRoles: ["doctor", "clinic"],
    },

    supplier: {
      id: "supplier",
      title: t("buyCredits.packages.supplierTitle"),
      credits: 100,
      currency: "TRY",
      basePrice: null,
      vatAmount: null,
      // Tedarikçi paketi tüm dillerde KDV DAHİL 1000 TL — backend payment/constants.py
      // PACKAGES["supplier"]["amount"] ile aynı tutulmalıdır.
      totalPrice: 1000,
      currencyNote: t("buyCredits.packages.currencyNote", { amount: 1000 }),
      priceLabel: t("buyCredits.packages.oneTime"),
      usageLines: [
        t("buyCredits.packages.supplierUsage1"),
        t("buyCredits.packages.supplierUsage2"),
        t("buyCredits.packages.durationMonths", { count: PACKAGE_DURATION_MONTHS }),
      ],
      allowedRoles: ["supplier"],
    },
  };
}

function formatMoney(amount) {
  const formatted = Number(amount).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} ₺`;
}

export default function BuyCreditsPage() {
  const { t, i18n } = useTranslation("common");

  const international = isInternationalLanguage(i18n.language);
  // Sunucunun 409 price_mismatch ile bildirdiği doctor_clinic tutarı; dil değişince sıfırlanır.
  const [serverTotal, setServerTotal] = useState(null);
  useEffect(() => {
    setServerTotal(null);
  }, [i18n.language]);
  const PACKAGES = useMemo(() => buildPackages(international, t, serverTotal), [international, t, serverTotal]);

  const CONTRACTS = useMemo(
    () => ({
      terms: { title: t("buyCredits.legal.terms"), file: "/contracts/abonelik-sozlesmesi.pdf" },
      kvkkAydinlatma: { title: t("buyCredits.kvkkAydinlatma"), file: "/contracts/kvkk-aydinlatma.pdf" },
      kvkkAcikRiza: { title: t("buyCredits.kvkkAcikRiza"), file: "/contracts/kvkk-acik-riza.pdf" },
      veriIsleyen: { title: t("buyCredits.veriIsleyen"), file: "/contracts/veri-isleyen-sozlesmesi.pdf" },
    }),
    [t]
  );

  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState("");
  const [profileLoading, setProfileLoading] = useState(true);

  const [selectedPkgId, setSelectedPkgId] = useState(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [needInvoice, setNeedInvoice] = useState(false);
  const [invoiceName, setInvoiceName] = useState("");
  const [invoiceCompany, setInvoiceCompany] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [taxNo, setTaxNo] = useState("");
  const [invoiceAddress, setInvoiceAddress] = useState("");

  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const [activeDoc, setActiveDoc] = useState(null);
  const [showKvkkMenu, setShowKvkkMenu] = useState(false);

  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const res = await axiosInstance.get("/api/auth/me/");
        const data = res.data;
        setProfile(data);
        setRole(normalizeRole(data?.role));

        if (data?.full_name) setFullName((prev) => prev || data.full_name);
        if (data?.email) setEmail((prev) => prev || data.email);
        if (data?.phone) setPhone((prev) => prev || data.phone);
      } catch (e) {
        console.log("PROFILE ERROR:", e?.response?.status, e?.message);
        setProfile(null);
        setRole("");
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const visiblePkgIds = useMemo(() => {
    if (!role) return [];
    return Object.keys(PACKAGES).filter((id) => PACKAGES[id].allowedRoles.includes(role));
  }, [role, PACKAGES]);

  useEffect(() => {
    if (!role) {
      setSelectedPkgId(null);
      return;
    }
    const firstAllowed = visiblePkgIds[0] || null;
    setSelectedPkgId(firstAllowed);
  }, [role, visiblePkgIds]);

  const selectedPkg = useMemo(() => {
    if (!selectedPkgId) return null;
    return PACKAGES[selectedPkgId] || null;
  }, [selectedPkgId, PACKAGES]);

  const total = selectedPkg?.totalPrice ?? 0;

  const currentCredits = Number(profile?.credit_balance ?? profile?.credits ?? 0);

  const openDocument = (key) => {
    setActiveDoc(CONTRACTS[key] || null);
    setShowKvkkMenu(false);
  };

  const closeDocument = () => {
    setActiveDoc(null);
  };

  const validate = () => {
    if (!fullName.trim()) return t("buyCredits.alert.fullNameRequired");
    if (!email.trim()) return t("buyCredits.alert.emailRequired");
    if (!agree) return t("buyCredits.alert.mustAgree");

    if (needInvoice) {
      if (!invoiceName.trim() && !invoiceCompany.trim()) {
        return t("buyCredits.alert.invoiceNameOrCompany");
      }
      if (!invoiceAddress.trim()) {
        return t("buyCredits.alert.invoiceAddressRequired");
      }
    }

    return "";
  };

  const handleCardPayment = async () => {
    if (loading) return;

    if (!role) {
      window.alert(t("buyCredits.roleUnavailable"));
      return;
    }

    if (!selectedPkg) {
      window.alert(t("buyCredits.noPackageForRole"));
      return;
    }

    if (!PACKAGES[selectedPkg.id].allowedRoles.includes(role)) {
      window.alert(t("buyCredits.packageMismatch"));
      return;
    }

    const err = validate();
    if (err) {
      window.alert(err);
      return;
    }

    setLoading(true);
    try {
      // NOT: dil/currency burada gönderilmiyor — backend bunlara güvenmez; fiyatı
      // kullanıcının hesabına kayıtlı preferred_language'a göre
      // kendisi belirler (bkz. accounts LanguageSwitch + payment/views.py
      // create_payment_order). Burada gösterilen fiyat/para birimi sadece
      // arayüz tahmini; gerçek tutar sunucu tarafında sabitlenir.
      // expected_total: ekranda gösterilen toplam. Fiyatı belirlemez; sunucu kendi
      // tutarıyla uyuşmazsa sipariş oluşturmaz ve 409 price_mismatch döner.
      const createRes = await axiosInstance.post("/api/payment/orders/create/", {
        package_id: selectedPkg.id,
        ...(selectedPkg.id === "doctor_clinic" ? { expected_total: Number(total).toFixed(2) } : {}),
      });

      const orderId = createRes?.data?.order_id;
      if (!orderId) {
        throw new Error(t("buyCredits.orderCreateFailed"));
      }

      const startRes = await axiosInstance.post(`/api/payment/orders/${orderId}/start/`);
      const paymentUrl = startRes?.data?.payment_url;

      if (!paymentUrl) {
        throw new Error(t("buyCredits.paymentLinkFailed"));
      }

      window.location.href = paymentUrl;
    } catch (err) {
      const data = err?.response?.data;
      const serverAmount = Number(data?.server_total);
      if (err?.response?.status === 409 && data?.code === "price_mismatch" && Number.isFinite(serverAmount)) {
        // Ekrandaki tutar sunucununkiyle uyuşmuyor: Moka'ya gitme, ekranı güncelle.
        setServerTotal(serverAmount);
        window.alert(t("buyCredits.priceUpdated", { amount: formatMoney(serverAmount) }));
        return;
      }
      console.log("CARD PAYMENT FAIL:", err?.response?.status, err?.response?.data || err.message);
      window.alert(err?.response?.data?.detail || t("buyCredits.cardPaymentFailedGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [1, 2, 3, 4].map((n) => ({
    q: t(`buyCredits.faq.q${n}`),
    a: t(`buyCredits.faq.a${n}`),
  }));

  return (
    <>
      <div className="page buy-credits">
        <header className="buy-credits__hero">
          <div className="buy-credits__hero-text">
            <h1 className="buy-credits__title">{t("buyCredits.title")}</h1>
            <p className="buy-credits__subtitle">{t("buyCredits.subtitle")}</p>
          </div>

          <div className="buy-credits__hero-boxes">
            <div className="credit-balance-box">
              <div className="credit-balance-box__icon">
                <FiGift aria-hidden="true" />
              </div>
              <div>
                <div className="credit-balance-box__label">{t("buyCredits.creditBalance.title")}</div>
                <div className="credit-balance-box__value">
                  {profileLoading ? "—" : currentCredits} <span>{t("buyCredits.packages.credits", { count: currentCredits })}</span>
                </div>
              </div>
            </div>

            <div className="credit-prompt-box">
              <div className="credit-prompt-box__icon">
                <FiGift aria-hidden="true" />
              </div>
              <div>
                <div className="credit-prompt-box__title">{t("buyCredits.morePrompt.title")}</div>
                <div className="credit-prompt-box__desc">{t("buyCredits.morePrompt.desc")}</div>
              </div>
            </div>
          </div>

          {profile?.membership_expired && (
            <div className="buy-credits__notice" role="alert">
              <FiInfo />
              <span>{t("buyCredits.membershipExpiredNotice")}</span>
            </div>
          )}
        </header>

        <section className="package-single-wrap">
          {profileLoading ? (
            <div className="package-single-status">{t("buyCredits.roleLoading")}</div>
          ) : !role || !selectedPkg ? (
            <div className="package-single-status">{t("buyCredits.roleUnavailable")}</div>
          ) : (
            <div className="package-single">
              <div className="package-single__icon">
                <FiCreditCard aria-hidden="true" />
              </div>

              <h2 className="package-single__title">{selectedPkg.title}</h2>

              <div className="package-single__credits">
                <strong>{selectedPkg.credits}</strong> {t("buyCredits.packages.credits", { count: selectedPkg.credits })}
              </div>

              <div className="package-single__price">
                <span className="package-single__amount">
                  {formatMoney(selectedPkg.totalPrice)}
                </span>
                <span className="package-single__per">{selectedPkg.priceLabel}</span>
              </div>

              {selectedPkg.currencyNote && (
                <div className="package-single__currency-note">{selectedPkg.currencyNote}</div>
              )}

              <ul className="package-single__features">
                {selectedPkg.usageLines.map((line) => (
                  <li key={line}>
                    <FiCheck aria-hidden="true" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="info-badges">
          <div className="info-badge">
            <div className="info-badge__icon info-badge__icon--blue">
              <FiZap aria-hidden="true" />
            </div>
            <div>
              <div className="info-badge__title">{t("buyCredits.badges.fastSecure.title")}</div>
              <div className="info-badge__desc">{t("buyCredits.badges.fastSecure.desc")}</div>
            </div>
          </div>

          <div className="info-badge">
            <div className="info-badge__icon info-badge__icon--green">
              <FiCheckCircle aria-hidden="true" />
            </div>
            <div>
              <div className="info-badge__title">{t("buyCredits.badges.instant.title")}</div>
              <div className="info-badge__desc">{t("buyCredits.badges.instant.desc")}</div>
            </div>
          </div>
        </section>

        <div className="buy-credits__grid">
          <section className="buy-credits__left">
            <div className="panel">
              <h2 className="panel__title">{t("buyCredits.contact.title")}</h2>

              <div className="form-grid">
                <div className="field">
                  <label>{t("buyCredits.contact.fullName")}</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t("buyCredits.contact.fullNamePh")}
                    autoComplete="name"
                  />
                </div>

                <div className="field">
                  <label>{t("buyCredits.contact.email")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("buyCredits.contact.emailPh")}
                    autoComplete="email"
                  />
                </div>

                <div className="field">
                  <label>{t("buyCredits.contact.phone")}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t("buyCredits.contact.phonePh")}
                    autoComplete="tel"
                  />
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="invoice-toggle">
                <label className="check">
                  <input type="checkbox" checked={needInvoice} onChange={(e) => setNeedInvoice(e.target.checked)} />
                  <span>{t("buyCredits.invoice.needInvoice")}</span>
                </label>
              </div>

              {needInvoice && (
                <div className="form-grid">
                  <div className="field">
                    <label>{t("buyCredits.invoice.name")}</label>
                    <input
                      type="text"
                      value={invoiceName}
                      onChange={(e) => setInvoiceName(e.target.value)}
                      placeholder={t("buyCredits.invoice.namePh")}
                    />
                  </div>

                  <div className="field">
                    <label>{t("buyCredits.invoice.company")}</label>
                    <input
                      type="text"
                      value={invoiceCompany}
                      onChange={(e) => setInvoiceCompany(e.target.value)}
                      placeholder={t("buyCredits.invoice.companyPh")}
                    />
                  </div>

                  <div className="field field--half">
                    <label>{t("buyCredits.invoice.taxOffice")}</label>
                    <input
                      type="text"
                      value={taxOffice}
                      onChange={(e) => setTaxOffice(e.target.value)}
                      placeholder={t("buyCredits.invoice.taxOfficePh")}
                    />
                  </div>

                  <div className="field field--half">
                    <label>{t("buyCredits.invoice.taxNo")}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={taxNo}
                      onChange={(e) => setTaxNo(e.target.value.replace(/[^\d]/g, "").slice(0, 11))}
                      placeholder={t("buyCredits.invoice.taxNoPh")}
                    />
                  </div>

                  <div className="field field--full">
                    <label>{t("buyCredits.invoice.address")}</label>
                    <textarea
                      rows={3}
                      value={invoiceAddress}
                      onChange={(e) => setInvoiceAddress(e.target.value)}
                      placeholder={t("buyCredits.invoice.addressPh")}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="panel">
              <label className="check" style={{ alignItems: "flex-start" }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span style={{ lineHeight: 1.6 }}>
                  {t("buyCredits.legal.agreePrefix")}{" "}
                  <button
                    type="button"
                    className="link"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    onClick={() => openDocument("terms")}
                  >
                    {t("buyCredits.legal.terms")}
                  </button>{" "}
                  {t("buyCredits.legal.and")}{" "}
                  <button
                    type="button"
                    className="link"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    onClick={() => setShowKvkkMenu((prev) => !prev)}
                  >
                    {t("buyCredits.legal.kvkk")}
                  </button>
                </span>
              </label>

              {showKvkkMenu && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#f9fafb",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("buyCredits.kvkkDocsTitle")}</div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button type="button" className="btn" onClick={() => openDocument("kvkkAydinlatma")}>
                      {t("buyCredits.kvkkAydinlatma")}
                    </button>
                    <button type="button" className="btn" onClick={() => openDocument("kvkkAcikRiza")}>
                      {t("buyCredits.kvkkAcikRiza")}
                    </button>
                    <button type="button" className="btn" onClick={() => openDocument("veriIsleyen")}>
                      {t("buyCredits.veriIsleyen")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          <aside className="buy-credits__right">
            <div className="summary">
              <h3 className="summary__title">{t("buyCredits.summary.title")}</h3>

              <div className="summary__row">
                <span>{t("buyCredits.summary.package")}</span>
                <strong>{selectedPkg ? `${selectedPkg.credits} ${t("buyCredits.packages.credits", { count: selectedPkg.credits })}` : "-"}</strong>
              </div>

              <div className="summary__divider" />

              <div className="summary__row summary__row--total">
                <span>{t("buyCredits.summary.total")}</span>
                <strong>{selectedPkg ? formatMoney(total) : "-"}</strong>
              </div>

              <button
                type="button"
                className="summary__pay"
                onClick={handleCardPayment}
                disabled={loading || !selectedPkg || !role || profileLoading}
              >
                {loading
                  ? t("buyCredits.redirecting", { amount: formatMoney(total) })
                  : t("buyCredits.payButton", { amount: formatMoney(total) })}
              </button>

              <p className="summary__footnote">
                <FiShield aria-hidden="true" />
                {t("buyCredits.footnote")}
              </p>
            </div>
          </aside>
        </div>

        <section className="faq">
          <h2 className="faq__title">{t("buyCredits.faq.title")}</h2>

          <div className="faq__list">
            {faqItems.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={idx} className={`faq-item ${isOpen ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="faq-item__question"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.q}</span>
                    <FiChevronDown aria-hidden="true" className="faq-item__chevron" />
                  </button>

                  {isOpen && <div className="faq-item__answer">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {activeDoc && (
        <div
          onClick={closeDocument}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1100px, 96vw)",
              height: "min(90vh, 900px)",
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 80px rgba(0,0,0,0.25)",
            }}
          >
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <strong>{activeDoc.title}</strong>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <a
                  href={activeDoc.file}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                >
                  {t("buyCredits.openInNewTab")}
                </a>

                <button
                  type="button"
                  onClick={closeDocument}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-label={t("buyCredits.close")}
                >
                  <FiX />
                </button>
              </div>
            </div>

            <iframe
              title={activeDoc.title}
              src={activeDoc.file}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#f3f4f6",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
